/**
 * Custom Header JavaScript
 * Handles mobile menu toggle and cart drawer integration
 */

class CustomHeader {
  constructor() {
    this.mobileMenuToggle = document.querySelector('.custom-header__mobile-menu-toggle');
    this.mobileMenu = document.querySelector('.custom-header__mobile-menu');
    this.mobileMenuClose = document.querySelector('.custom-header__mobile-menu-close');
    this.mobileMenuOverlay = document.querySelector('.custom-header__mobile-menu-overlay');
    this.cartToggle = document.querySelector('.custom-header__cart-toggle');
    this.cartCount = document.querySelector('#custom-cart-count');
    this.desktopMegaMenu = document.querySelector('#custom-desktop-mega-menu');
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
    this.handleResize();
    this.initMegaReviewsCarousel();
    this.initMobileReviewsCarousel();
  }

  bindEvents() {
    // Menu toggle (mobile drawer or desktop mega menu)
    if (this.mobileMenuToggle) {
      this.mobileMenuToggle.addEventListener('click', () => {
        if (window.innerWidth >= 741 && this.desktopMegaMenu) {
          this.toggleDesktopMegaMenu();
        } else {
          this.openMobileMenu();
        }
      });
    }

    // Mobile menu close
    if (this.mobileMenuClose) {
      this.mobileMenuClose.addEventListener('click', () => this.closeMobileMenu());
    }

    // Mobile menu overlay click
    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', () => this.closeMobileMenu());
    }

    // Cart toggle
    if (this.cartToggle) {
      this.cartToggle.addEventListener('click', () => this.openCartDrawer());
    }

    // Listen for cart updates
    document.addEventListener('cart:changed', () => this.updateCartCount());
    
    // Listen for cart drawer close events
    document.addEventListener('drawer:close', () => this.handleCartDrawerClose());
    
    // Monitor drawer attribute changes to detect when it's closed by other means
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const drawer = mutation.target;
          if (drawer.matches('cart-drawer, #mini-cart') && !drawer.hasAttribute('open')) {
            // Drawer was closed, restore scroll
            setTimeout(() => this.restoreBodyScroll(), 100);
          }
        }
      });
    });
    
    // Start observing the drawer
    const cartDrawer = document.querySelector('cart-drawer, #mini-cart');
    if (cartDrawer) {
      observer.observe(cartDrawer, { attributes: true });
    }
    
    // Listen for escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (this.mobileMenu && this.mobileMenu.classList.contains('active')) {
          this.closeMobileMenu();
        }
        if (this.desktopMegaMenu && this.desktopMegaMenu.classList.contains('active')) {
          this.closeDesktopMegaMenu();
        }
        // Also close cart drawer if open
        const cartDrawer = document.querySelector('cart-drawer[open], #mini-cart[open]');
        if (cartDrawer) {
          cartDrawer.removeAttribute('open');
          this.restoreBodyScroll();
        }
      }
    });

    // Close mega menu when clicking outside
    document.addEventListener('click', (e) => {
      if (this.desktopMegaMenu && this.desktopMegaMenu.classList.contains('active')) {
        if (!e.target.closest('.custom-header') && !e.target.closest('.custom-header__desktop-mega-menu')) {
          this.closeDesktopMegaMenu();
        }
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
    
    // Listen for clicks on drawer overlay to close cart
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('drawer__overlay')) {
        const cartDrawer = document.querySelector('cart-drawer[open], #mini-cart[open]');
        if (cartDrawer) {
          cartDrawer.removeAttribute('open');
          this.restoreBodyScroll();
        }
      }
    });
  }

  openMobileMenu() {
    if (this.mobileMenu) {
      this.mobileMenu.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Focus management for accessibility
      const firstFocusableElement = this.mobileMenu.querySelector('button, a');
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    }
  }

  closeMobileMenu() {
    if (this.mobileMenu) {
      this.mobileMenu.classList.remove('active');
      document.body.style.overflow = '';
      
      // Return focus to toggle button
      if (this.mobileMenuToggle) {
        this.mobileMenuToggle.focus();
      }
    }
  }

  openCartDrawer() {
    // Check if cart drawer exists and open it using the proper method
    const cartDrawer = document.querySelector('cart-drawer, #mini-cart');
    if (cartDrawer) {
      // Set the open attribute which triggers the drawer's built-in functionality
      cartDrawer.setAttribute('open', '');
      
      // Dispatch a custom event to notify other components
      document.dispatchEvent(new CustomEvent('cart:open', {
        bubbles: true,
        detail: { source: 'custom-header' }
      }));
      
      // Ensure body overflow is hidden when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      // Try to trigger cart opening via existing cart buttons as fallback
      const existingCartButton = document.querySelector('[data-action="open-cart"], .header__cart-count, .cart-count-bubble');
      if (existingCartButton) {
        existingCartButton.click();
      } else {
        // Final fallback to cart page
        window.location.href = '/cart';
      }
    }
  }

  updateCartCount() {
    // Get cart count from Shopify
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        if (this.cartCount) {
          this.cartCount.textContent = cart.item_count;
          this.cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
        
        // Also update other cart count elements that might exist
        const allCartCounts = document.querySelectorAll('.header__cart-count, cart-count');
        allCartCounts.forEach(element => {
          element.textContent = cart.item_count;
          if (cart.item_count > 0) {
            element.classList.remove('hidden');
          } else {
            element.classList.add('hidden');
          }
        });
      })
      .catch(error => {
        console.error('Error updating cart count:', error);
      });
  }

  handleResize() {
    // Close desktop mega menu on resize to mobile
    if (window.innerWidth < 741 && this.desktopMegaMenu && this.desktopMegaMenu.classList.contains('active')) {
      this.closeDesktopMegaMenu();
    }
    // Close mobile menu on resize to desktop
    if (window.innerWidth >= 741 && this.mobileMenu && this.mobileMenu.classList.contains('active')) {
      this.closeMobileMenu();
    }
  }

  toggleDesktopMegaMenu() {
    if (this.desktopMegaMenu.classList.contains('active')) {
      this.closeDesktopMegaMenu();
    } else {
      this.openDesktopMegaMenu();
    }
  }

  openDesktopMegaMenu() {
    if (this.desktopMegaMenu) {
      this.desktopMegaMenu.classList.add('active');
      this.desktopMegaMenu.removeAttribute('hidden');
      
      // Update button aria-expanded
      if (this.mobileMenuToggle) {
        this.mobileMenuToggle.setAttribute('aria-expanded', 'true');
      }
    }
  }

  closeDesktopMegaMenu() {
    if (this.desktopMegaMenu) {
      this.desktopMegaMenu.classList.remove('active');
      
      // Wait for transition to complete before hiding
      setTimeout(() => {
        if (!this.desktopMegaMenu.classList.contains('active')) {
          this.desktopMegaMenu.setAttribute('hidden', '');
        }
      }, 300);
      
      // Update button aria-expanded
      if (this.mobileMenuToggle) {
        this.mobileMenuToggle.setAttribute('aria-expanded', 'false');
      }
    }
  }

  restoreBodyScroll() {
    // Comprehensive scroll restoration
    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.body.style.position = '';
    
    // Remove any overflow-related classes
    document.body.classList.remove('drawer-open', 'no-scroll', 'overflow-hidden');
    
    // Force a reflow to ensure changes take effect
    document.body.offsetHeight;
  }

  handleCartDrawerClose() {
    this.restoreBodyScroll();
    
    // Dispatch close event
    document.dispatchEvent(new CustomEvent('cart:close', {
      bubbles: true,
      detail: { source: 'custom-header' }
    }));
  }

  initMegaReviewsCarousel() {
    const carousel = document.querySelector('[data-carousel="mega-reviews"]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.custom-header__mega-review-slide');
    const dots = carousel.querySelectorAll('.custom-header__mega-dot');
    let currentSlide = 0;
    let autoplayInterval;

    const showSlide = (index) => {
      // Hide all slides
      slides.forEach(slide => slide.classList.remove('active'));
      dots.forEach(dot => dot.classList.remove('active'));
      
      // Show current slide
      if (slides[index]) {
        slides[index].classList.add('active');
        dots[index]?.classList.add('active');
      }
      
      currentSlide = index;
    };

    const nextSlide = () => {
      const next = (currentSlide + 1) % slides.length;
      showSlide(next);
    };

    const startAutoplay = () => {
      if (slides.length > 1) {
        autoplayInterval = setInterval(nextSlide, 4000); // Change slide every 4 seconds
      }
    };

    const stopAutoplay = () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    };

    // Dot navigation
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        showSlide(index);
        setTimeout(startAutoplay, 2000); // Restart autoplay after 2 seconds
      });
    });

    // Pause autoplay when hovering over carousel
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);

    // Start autoplay
    startAutoplay();
  }

  initMobileReviewsCarousel() {
    const carousel = document.querySelector('[data-carousel="mobile-reviews"]');
    if (!carousel) return;

    const slides = carousel.querySelectorAll('.custom-header__mobile-review-slide');
    const dots = carousel.querySelectorAll('.custom-header__mobile-dot');
    let currentSlide = 0;
    let autoplayInterval;

    const showSlide = (index) => {
      // Hide all slides
      slides.forEach(slide => slide.classList.remove('active'));
      dots.forEach(dot => dot.classList.remove('active'));
      
      // Show current slide
      if (slides[index]) {
        slides[index].classList.add('active');
        dots[index]?.classList.add('active');
      }
      
      currentSlide = index;
    };

    const nextSlide = () => {
      const next = (currentSlide + 1) % slides.length;
      showSlide(next);
    };

    const startAutoplay = () => {
      if (slides.length > 1) {
        autoplayInterval = setInterval(nextSlide, 4000); // Change slide every 4 seconds
      }
    };

    const stopAutoplay = () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    };

    // Dot navigation
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        stopAutoplay();
        showSlide(index);
        setTimeout(startAutoplay, 2000); // Restart autoplay after 2 seconds
      });
    });

    // Touch/swipe support for mobile
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    carousel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      stopAutoplay();
    });

    carousel.addEventListener('touchend', (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      
      const deltaX = startX - endX;
      const deltaY = startY - endY;
      
      // Only process horizontal swipes (ignore vertical scrolling)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
        if (deltaX > 0) {
          // Swipe left - next slide
          nextSlide();
        } else {
          // Swipe right - previous slide
          const prev = (currentSlide - 1 + slides.length) % slides.length;
          showSlide(prev);
        }
      }
      
      setTimeout(startAutoplay, 2000); // Restart autoplay after 2 seconds
    });

    // Pause autoplay when hovering over carousel (for devices with hover)
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);

    // Start autoplay
    startAutoplay();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.customHeaderInstance = new CustomHeader();
  
  // Listen for cart changes and update count
  document.addEventListener('cart:refresh', () => {
    if (window.customHeaderInstance) {
      window.customHeaderInstance.updateCartCount();
    }
  });
  
  // Listen for custom cart change events
  document.addEventListener('cart:changed', () => {
    if (window.customHeaderInstance) {
      window.customHeaderInstance.updateCartCount();
    }
  });
  
  // Additional cart drawer close button listener
  // This ensures the drawer close button works properly
  document.addEventListener('click', (e) => {
    if (e.target.closest('.drawer__close-button')) {
      const cartDrawer = document.querySelector('cart-drawer[open], #mini-cart[open]');
      if (cartDrawer) {
        cartDrawer.removeAttribute('open');
        
        // Use the custom header instance to restore scroll
        if (window.customHeaderInstance) {
          window.customHeaderInstance.restoreBodyScroll();
        } else {
          // Fallback scroll restoration
          document.body.style.overflow = '';
          document.body.style.overflowY = '';
          document.body.style.position = '';
          document.body.classList.remove('drawer-open', 'no-scroll', 'overflow-hidden');
        }
        
        // Dispatch close event
        document.dispatchEvent(new CustomEvent('cart:close', {
          bubbles: true,
          detail: { source: 'drawer-close-button' }
        }));
      }
    }
  });
});

// Global function to update cart item count
window.updateCartItemCount = function() {
  if (window.customHeaderInstance) {
    window.customHeaderInstance.updateCartCount();
  }
};

// Cart integration functions
window.customHeaderCartIntegration = {
  // Function to be called when items are added to cart
  onCartAdd: function(item) {
    const customHeader = document.querySelector('.custom-header');
    if (customHeader) {
      // Update cart count
      const event = new CustomEvent('cart:changed');
      document.dispatchEvent(event);
      
      // Optional: Show a brief animation or notification
      const cartToggle = customHeader.querySelector('.custom-header__cart-toggle');
      if (cartToggle) {
        cartToggle.classList.add('cart-updated');
        setTimeout(() => {
          cartToggle.classList.remove('cart-updated');
        }, 1000);
      }
    }
  },

  // Function to be called when cart is updated
  onCartUpdate: function(cart) {
    const event = new CustomEvent('cart:changed');
    document.dispatchEvent(event);
  }
};

// Add cart animation CSS
const style = document.createElement('style');
style.textContent = `
  .custom-header__cart-toggle.cart-updated {
    animation: cartPulse 0.5s ease-in-out;
  }
  
  @keyframes cartPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;
document.head.appendChild(style);
